//AutoCompleteMUI.tsx =================================================
import "../Util/Prototypes";

import * as AutoCompleteMUI from "./AutoCompleteMUI";
import * as QuickSearch from "./QuickSearch";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";

import { Autocomplete, Popper, TextField } from "@mui/material";
import { AutocompleteChangeReason } from "@mui/material";

import React from "react";

import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

export const suggestField: string = "Suggest";
export const layerField: string = "LayerField";

const refreshRate: number = 300; // Milliseconds.
let startsWith: boolean;

let prevId: number = 0;
let currentValue: iOption = { label: "", id: -1 };
let forceInput: boolean = false;
let enableEnterKey: boolean = false;
let textFieldRef: HTMLElement;
let startValue: string = "";

interface iProps {
   initialValue?: string;  //Initial label value.
   width?: number;         //Must be an integer.
   backgroundColor?: string;
   highlightColor?: string;
   toMixedCase?: boolean;  //Convert to proper case.
   tooltip?: string;       //Overall tooltip.
   focusMe?: boolean;      //Focus on startup.
   children?: any;         //Placeholder hint text.
   onSelected(value: any, exactSearch: boolean): void;  //Selected value changed function.
}

interface iWord {
   value: string; // Word text.
   type: string;  // "Normal", "Bold", "LayerName", "AliasName".
}

interface iOption {
   label: string;     // Raw label string.
   id: number;        // 0 to 9 if history or 1 to 10 if not.
   words?: iWord[];   // Words that make up the suggestion for rendering.
   outValue?: string; // LayerName|FieldName|Value
}

//Render function.
export function Render(props: iProps) {
   //Util.errMsg("2) Top of AutoComplete Render Initial Value: " + QuickSearch.forceUpdate + " " + props.initialValue);

   const [value, setValue] = React.useState({ label: "", id: -1 });
   const [inputValue, setInputValue] = React.useState("");
   const [options, setOptions] = React.useState([]);

   //Force initial input textbox value.
   if (QuickSearch.forceUpdate) {
      QuickSearch.setForceUpdate(false);
      forceInput = true;
      //Util.errMsg("Forcing Initial Value: " + props.initialValue);

      //Clear input textbox if field name dropdown changed.
      if (props.initialValue === "") {
         if (inputValue) {
            currentValue = { label: "", id: -1 };
            setValue({ label: "", id: -1 }); //Clear input textbox.
         }
         if (props.focusMe) {
            setTimeout(function () { textFieldRef.focus(); }, 300);
         }

      } else { //Set input textbox to history value.
         currentValue = { label: props.initialValue, id: -1 };
      }
   }

   //Component did mount. Only fires when quickSearch mounts.
   React.useEffect(() => {
      //Util.errMsg("Did Mount Initial Value: " + QuickSearch.forceUpdate + " "  + props.initialValue);

      //Restore previous search value.
      if (currentValue && currentValue.label) {
         //Util.errMsg("Restoring previous value: " + currentValue.label + " " + currentValue.id);
         setValue({ label: currentValue.label, id: currentValue.id });
         startValue = currentValue.label;
      }
      setTimeout(function () { enableEnterKey = true; }, 300);

      //Component will unmount.
      return () => {
      };
   }, []);

   //Component did update. Only fires when quickSearch mounts or autoComplete refreshes.
   React.useEffect(() => {
      //Util.errMsg("Did Update Initial Value: " + QuickSearch.forceUpdate + " " + props.initialValue);

      //Clear the popup options menu when input textbox is blank.
      if (!inputValue) {
         setTimeout(function () { setOptions([]); }, 400);
         return undefined;
      }

      //Get suggestion(s) from server.
      prevId++;

      if (!forceInput) {
         //Util.errMsg("*** Getting Server Data: " + QuickSearch.forceUpdate + " " + inputValue);
         setTimeout(function () { getServerData(inputValue.trim(), prevId); }, refreshRate);
      }

      forceInput = false;

   }, [value, inputValue]);

   //Get server data.
   function getServerData(searchText: string, id: number) {

      if (id === prevId) {
         //Set the layer dash field string.
         let layerDashField: string = "";
         if (QuickSearch.layerNameDropdownValue !== "") {
            layerDashField = QuickSearch.layerNameDropdownValue + "-" + QuickSearch.fieldNameDropdownValue;
         }

         //See if search string starts with a number.
         const firstChar: string = searchText.charAt(0);
         const lastChar: string = searchText.charAt(searchText.length - 1);
         const startsWithNumber: boolean = firstChar.IsNumeric();
         const endsWithNumber: boolean = lastChar.IsNumeric();

         //Set contains all the words or starts-with.
         startsWith = false;
         if ((startsWithNumber && endsWithNumber) || (QuickSearch.fieldNameDropdownValue.toLowerCase() === Settings.pin_fieldName.toLowerCase()) || (searchText.length < 4)) {
            startsWith = true;
         }

         //Form sql string.
         const sql: string = getAutoCompleteSimpleSearchSQL(layerDashField, searchText);

         //Initialize query.
         const query = new Query({
            returnGeometry: false,
            //outFields: [AutoCompleteMUI.suggestField, AutoCompleteMUI.layerField],
            outFields: ["*"],
            where: sql,
            start: 0,
            num: 10,
         });

         //Execute query.
         QueryTask.executeQueryJSON(Settings.autoCompleteURL, query).then(queryTask_success).catch(queryTask_failed);
      }
   }

   //Query task completed.
   function queryTask_success(response: __esri.FeatureSet) {
      //Util.logMsg("Response returned from Server", response);
      const features = response.features;
      const fields = response.fields;
      let atr: any;
      let layField: Util_App.iLayerField;
      let suggest: string;
      let aliasName: string;
      let words: iWord[];
      let outValue: string;
      const newOptions: iOption[] = [];

      //Quit if no fields were returned from arcgis online.
      if (!fields) {
         setOptions(newOptions);
         return;
      }

      //Validate autocomplete table field names.
      let suggestFieldFound: boolean = false;
      let layerFieldFound: boolean = false;

      for (let i: number = 0; i < 3; i++) {
         if (fields[i].name === AutoCompleteMUI.suggestField) {
            suggestFieldFound = true;
         }
         if (fields[i].name === AutoCompleteMUI.layerField) {
            layerFieldFound = true;
         }
      }

      if (!suggestFieldFound || !layerFieldFound) {
         Util.errMsg('AutoComplete table field names must be named: "' + AutoCompleteMUI.suggestField + '" and "' + AutoCompleteMUI.layerField + '"');
         return;
      }

      //Process all records.
      let ii: number = 0;

      for (const feature of features) {
         ii++;
         atr = feature.attributes;
         layField = Util_App.getLayerAndFieldFromLayerDashField(atr[AutoCompleteMUI.layerField]);
         aliasName = Util_App.getSimpleSearchAliasByFieldName(layField.layerName, layField.fieldName);

         suggest = atr[AutoCompleteMUI.suggestField].SubStr(0, atr[AutoCompleteMUI.suggestField].indexOf("~")).trim();
         if (props.toMixedCase) {
            suggest = suggest.ToProperCase();
         }

         outValue = layField.layerName + "|" + layField.fieldName + "|" + suggest;
         words = highlightWords(inputValue, suggest);

         //Determine if layer name is to be shown in suggestion.
         if (QuickSearch.layerNameDropdownValue === "") {
            words.Add({ value: " " + layField.layerName, type: "LayerName" });
            words.Add({ value: " " + aliasName, type: "AliasName" });
         }
         newOptions.Add({ label: suggest, id: ii, words: words, outValue: outValue });
      }

      //Add first history record match.
      if (QuickSearch.history.length > 0) {
         const ac: string[] = [];
         for (const opt of newOptions) {
            ac.Add(opt.label); // Load temp array of autocomplete suggestion(s).
         }

         for (const hist of QuickSearch.history) {
            if ((QuickSearch.layerName === "") || (QuickSearch.layerName === hist.layerName)) {

               if ((hist.searchText.StartsWith(inputValue)) && (!ac.Contains(hist.searchText))) {
                  words = highlightWords(inputValue, hist.searchText);

                  //Determine if layer name is to be shown in suggestion.
                  if (QuickSearch.layerNameDropdownValue === "") {
                     aliasName = Util_App.getSimpleSearchAliasByFieldName(hist.layerName, hist.fieldName);
                     words.Add({ value: " " + hist.layerName, type: "LayerName" });
                     words.Add({ value: " " + aliasName, type: "AliasName" });
                  }

                  outValue = hist.layerName + "|" + hist.fieldName + "|" + hist.searchText;
                  newOptions.InsertAt(0, { label: hist.searchText, id: 0, words: words, outValue: outValue });

                  if (newOptions.length > 10) {
                     newOptions.pop();
                  }
                  break;
               }
            }
         }
      }

      setOptions(newOptions);
   }

   //Query task failed.
   function queryTask_failed(error: Error) {
      Util.errMsg("AutoComplete - QueryTask failed", error);
   }

   //Input change event.
   function handleInputChange(newInputValue: string) {
      //Util.errMsg("Inputing: " + startValue + " " + newInputValue);

      if (newInputValue && (startValue !== newInputValue)) {
         enableEnterKey = false;
      } else {
         enableEnterKey = true;
      }
      currentValue = { label: newInputValue, id: -1 };
      setInputValue(newInputValue);
      QuickSearch.setSearchText(newInputValue);
   }

   //Highlight change event.
   function handleHighlightChange(option: iOption | null, reason: string) {
      if (reason === "keyboard") {
         if (option && option.label) {
            //Util.errMsg("Highlighting: " + option.label + " Reason: " + reason);
            currentValue = { label: option.label, id: option.id, words: option.words, outValue: option.outValue };
         }
      }
   }

   //Selected value changed event.
   function handleChange(event: React.SyntheticEvent<Element, Event>, newValue: any, reason: AutocompleteChangeReason) {
      setOptions(newValue ? [newValue, ...options] : options);
      setValue(newValue);

      if (newValue) {
         //Util.errMsg("SSSSSSS Selected Value: " + newValue.label, newValue);
         let selectedValue: string;

         if (reason === "selectOption") {
            selectedValue = newValue.outValue;
            props.onSelected(selectedValue, true);

         } else { // User hit enter key on textbox, not the popup list.
            selectedValue = "||" + newValue;
            if (QuickSearch.layerNameDropdownValue !== "") {
               selectedValue = QuickSearch.layerNameDropdownValue + "|" + QuickSearch.fieldNameDropdownValue + "|" + newValue;
            }
            props.onSelected(selectedValue, false);
         }
      }
   }

   //Suggest popup menu open event.
   function handleOpen() {
      enableEnterKey = true;
   }

   //Tab key event.
   function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      //Util.errMsg("TTTTTTTT Tab Value: " + enableEnterKey + " " + currentValue.id);

      if (event.key === "Tab" || (enableEnterKey && event.key === "Enter" && currentValue.id === -1)) {
         if (currentValue.label.trim() !== "~^~") {
            let selectedValue: string;

            //Not an exact search.
            if (currentValue.id === -1) {
               selectedValue = "||" + currentValue.label;
               if (QuickSearch.layerNameDropdownValue !== "") {
                  selectedValue = QuickSearch.layerNameDropdownValue + "|" + QuickSearch.fieldNameDropdownValue + "|" + currentValue.label;
               }
               props.onSelected(selectedValue, false);

            } else { //Exact search.
               setValue({ label: currentValue.label, id: currentValue.id }); //Sets the input textbox value and options list.
               selectedValue = currentValue.outValue;
               props.onSelected(selectedValue, true);
            }
         }
      }
   }

   // autoComplete - The completion string appears inline after the input cursor.
   // filterOptions - Required for search by character. filterOptions = {(x) => x}
   // freeSolo - Required for search by character.
   // getOptionLabel - Sets the text value for the suggestion(s) list.
   // inputValue - The users input text.
   // isOptionEqualToValue - Compares suggestion text to input value text.
   // loading = True means component is in the loading state.
   // openOnFocus - True means the suggestion(s) will open on focus.
   // options - Contains the suggestion(s) list.
   // renderInput - The HTML for the component.
   // size - 'small', 'medium'.
   // value - The selected value.

   // open - True means the dropdown suggestion(s) popup is visible.
   // onOpen - The function called when the suggestion(s) popup requests to be opened.
   // onClose - The function called when the suggestion(s) popup requests to be closed.
   // onChange - Fired when a suggestion is selected.

   //Util.errMsg("Bottom of AutoComplete Render before return: " + QuickSearch.forceUpdate + " " + props.initialValue );

   //Get render option.
   function getRenderOption(option: iOption): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      for (const word of option.words) {
         if (word.type === "Bold") {
            jsx.push(
               <span key={Util.uid()} style={{ fontWeight: 600 }}>{word.value}</span>,
            );
         } else if (word.type === "LayerName") {
            jsx.push(
               <span key={Util.uid()} style={{ color: '#9F3333' }}><i>{word.value}</i></span>,
            );
         } else if (word.type === "AliasName") {
            jsx.push(
               <span key={Util.uid()}>{word.value}</span>,
            );
         } else {
            jsx.push(
               <span key={Util.uid()}>{word.value}</span>,
            );
         }
      }

      return (jsx);
   }

   //Set popup menu width.
   const customPopper = function (props3) {
      return (<Popper {...props3} style={{ width: Settings.leftPanelWidth }}></Popper>);
   };

   return (
      <Autocomplete
         id={"auto-complete-id"}
         sx={{ width: props.width, bgcolor: "white" }}
         filterOptions={(x) => x}
         options={options}
         autoComplete
         includeInputInList
         value={value}
         freeSolo={true}
         PopperComponent={customPopper}

         onInputChange={(event, newInputValue) => {
            handleInputChange(newInputValue);
         }}

         onHighlightChange={(event, option, reason) => {
            handleHighlightChange(option, reason);
         }}

         onChange={(event, newValue, reason) => {
            handleChange(event, newValue, reason);
         }}

         onKeyDown={(event) => {
            handleKeyDown(event);
         }}

         onOpen={(event) => {
            handleOpen();
         }}

         renderInput={(params) => (
            <TextField
               {...params}
               id="Quick-Search"
               label="Search"
               title={props.tooltip}
               placeholder={props.children}
               autoFocus={props.focusMe}
               autoComplete="off"
               inputRef={input => {
                  textFieldRef = input;
               }}
            />
         )}

         renderOption={(props2, option) => {
            return (
               <li {...props2} style={{ fontSize: "0.95rem" }}>
                  <div>
                     {getRenderOption(option)}
                  </div>
               </li>
            );
         }}

      />
   ); //end return.
} //end function.

//AutoComplete for Simple Search.
function getAutoCompleteSimpleSearchSQL(layerDashField: string, value: string): string {
   let sql: string;

   if (startsWith) {
      sql = Util_App.buildQueryClause(AutoCompleteMUI.suggestField, "begins with", value);

   } else {
      sql = Util_App.buildQueryClause(AutoCompleteMUI.suggestField, "contains all the words", value);
   }

   //Add layer and field name filter.
   if (layerDashField !== "") {
      if (layerDashField.Contains("%")) {
         sql += " and " + AutoCompleteMUI.layerField + " Like '" + layerDashField + "'";
      } else {
         sql += " and " + AutoCompleteMUI.layerField + " = '" + layerDashField + "'";
      }
   }
   return sql;
}

//Build array of words to be highlighted.
function highlightWords(searchText: string, suggest: string): iWord[] {

   //Initialize locals.
   const words: iWord[] = [];
   const maxSearchText: number = 100;
   let j: number;
   let k: number;
   let pos: number;
   let txt: string;
   let word: string;
   let reg: any;
   let matchText: string;
   let matchFound: boolean;
   const matchArray: boolean[] = [];
   let highlighting: boolean = false;
   let normalTexting: boolean = false;
   let myRun: string = "";

   //Get the search text and split into words.
   const textBoxWords: string[] = Util_App.cleanAndSplit(searchText, 15);

   //Clear all character positions that match users text.
   const numChar: number = Math.min(maxSearchText, suggest.length);
   for (j = 0; j < numChar; j++) {
      matchArray[j] = false;
   }

   //Starts with a number, so highlight starting characters only.
   matchFound = false;

   if (startsWith) {
      if (suggest.toLowerCase().StartsWith(searchText.toLowerCase())) {
         matchFound = true;

         for (j = 0; j < Math.min(searchText.length, maxSearchText); j++) {
            matchArray[j] = true;
         }
      }

   } else { //Highlight all words that match users search text.
      for (word of textBoxWords) {
         pos = suggest.toLowerCase().indexOf(word.toLowerCase());
         if (pos !== -1) {
            matchText = "";
            for (j = 0; j < word.length; j++) {
               matchText += "~";
            }
            reg = new RegExp(word.toLowerCase(), "g");
            txt = suggest.toLowerCase().replace(reg, matchText);

            matchFound = true;
            j = 0;

            for (k = 0; k < txt.length; k++) {
               if (txt.charAt(k) === "~") {
                  if (j < maxSearchText - 1) {
                     matchArray[j] = true;
                  }
               }
               j += 1;
            }
         }
      }
   }

   //Build runs.
   if (matchFound) {
      for (j = 0; j < Math.min(suggest.length, maxSearchText); j++) {

         //Character matches.
         if (matchArray[j]) {
            if (normalTexting) {
               normalTexting = false;
               words.Add({ value: myRun, type: "Bold" });
            }

            if (!highlighting) {
               myRun = ""; //Start new run.
               highlighting = true;
            }

         } else { //Character does not match.
            if (highlighting) {
               highlighting = false;
               words.Add({ value: myRun, type: "Normal" });
            }

            if (!normalTexting) {
               myRun = ""; //Start new run.
               normalTexting = true;
            }
         }
         myRun += suggest.SubStr(j, 1);
      }

      //Add last run.
      if (myRun !== "") {
         if (highlighting) {
            words.Add({ value: myRun, type: "Normal" });
         } else {
            words.Add({ value: myRun, type: "Bold" });
         }
      }
      highlighting = false;
      normalTexting = false;

   } else { //Nothing to highlight.
      words.Add({ value: suggest, type: "Bold" });
   }

   return words;
}
